import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import { Layout, Table, Tag, Modal, Button, Input, Select, DatePicker, Row, Col, Card, notification, Typography } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import './Registrations.css';
import moment from 'moment-timezone';

const { Content } = Layout;
const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const testTypes = [
  { name: "GED", subtypes: ["Mathematical Reasoning", "Reasoning Through Language Arts", "Science", "Social Studies"] },
  { name: "IGCSE", subtypes: ["Mathematics", "English", "Sciences (Biology, Chemistry, Physics)", "History", "Geography", "Foreign Languages", "Art & Design", "Business Studies", "Computer Science"] },
  { name: "A-LEVEL", subtypes: ["Mathematics", "Further Mathematics", "Physics", "Chemistry", "Biology", "English Literature", "History", "Geography", "Economics", "Psychology"] },
  { name: "SAT", subtypes: ["SAT (General)", "SAT Subject Tests"] },
  { name: "IELTS", subtypes: ["IELTS Academic", "IELTS General Training"] },
  { name: "AP", subtypes: ["AP Calculus AB", "AP Calculus BC", "AP Physics", "AP Chemistry", "AP Biology", "AP English Language and Composition", "AP English Literature and Composition", "AP U.S. History", "AP World History", "AP Psychology"] },
  { name: "IB", subtypes: ["IB Mathematics", "IB Physics", "IB Chemistry", "IB Biology", "IB English A", "IB English B", "IB History", "IB Economics", "IB Psychology", "Theory of Knowledge (TOK)"] },
  { name: "TOEFL", subtypes: ["TOEFL iBT", "TOEFL PBT"] },
  { name: "TOEFL-MUIC", subtypes: ["General", "IBT"] },
  { name: "CU-AAT", subtypes: ["ธรรมดา", "E-Testing"] },
  { name: "CU-ATS", subtypes: ["Physics", "Chemistry"] },
  { name: "CU-TEP", subtypes: ["ธรรมดา + Speaking", "E-Testing", "E-Testing + CU-TEP E-Testing Speaking"] },
  { name: "TU-GET", subtypes: ["PBT", "CBT"] },
  { name: "OTHERS", subtypes: [] }
];

const TestScore = () => {
  const { t } = useTranslation();
  const [testScores, setTestScores] = useState([]);
  const [students, setStudents] = useState([]);
  const [formData, setFormData] = useState({
    test_date: '',
    test_name: null, // Allow null value for Details
    score: '',
    type: '',
    sub_type: '',
    student_id: '',
    note: ''
  });
  const [error, setError] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [selectedTestType, setSelectedTestType] = useState('');
  const itemsPerPage = 100;
    const token = localStorage.getItem("token");
          let userRole = null;
          let userBranchId = null;
      
          if (token) {
          try {
              const decoded = jwtDecode(token);
              userRole = decoded.role;
              // Support both formats just in case
              userBranchId = decoded.branchId || decoded.branch_id || null;
          } catch (err) {
              console.error("Invalid token", err);
          }
          }
  const API_URL = process.env.REACT_APP_API_URL;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = moment.tz(dateString, 'Asia/Bangkok'); // Convert to Bangkok timezone
    return date.isValid() ? date.format('DD MMM YY').toUpperCase() : 'Invalid date';
  };

  const fetchTestScores = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/test-scores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestScores(response.data);
    } catch (error) {
      console.error('Error fetching test scores:', error);
    }
  }, [API_URL]);

  useEffect(() => {
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to fetch branches", err);
    }
  };

  fetchBranches();
}, [API_URL]);

  const fetchStudents = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/test-scores/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchTestScores();
    fetchStudents();
  }, [fetchTestScores, fetchStudents]);

  const handleSubmit = async () => {
  try {
    const token = localStorage.getItem('token');

    // formData.test_date is already "YYYY-MM-DD"
    const formDataToSubmit = {
      ...formData,
      test_date: formData.test_date || null,
    };

    await axios.post(`${API_URL}/test-scores`, formDataToSubmit, {
      headers: { Authorization: `Bearer ${token}` }
    });

    setFormData({
      test_date: '',
      test_name: null,
      score: '',
      type: '',
      sub_type: '',
      student_id: '',
      note: ''
    });
    setIsModalVisible(false);
    fetchTestScores();
  } catch (error) {
    console.error('Error submitting test score:', error);
    setError(t('Error submitting test score'));
  }
};

  const handleFormChange = (key, value) => {
    setFormData((prevData) => ({ ...prevData, [key]: value }));
  };

  const getSubtypes = () => {
    const selectedTestType = testTypes.find((type) => type.name === formData.type);
    return selectedTestType ? selectedTestType.subtypes : [];
  };

  const getFilteredData = useCallback(() => {
  return testScores.filter(score => {
    const fullName = `${score.firstname} ${score.lastname} ${score.nickname}`.toLowerCase();
    const searchMatch = !searchText || fullName.includes(searchText.toLowerCase());

    const typeMatch = !selectedTestType || score.type === selectedTestType;

    let dateMatch = true;
    if (selectedDateRange && selectedDateRange.length === 2) {
      const testDate = moment.tz(score.test_date, 'Asia/Bangkok');
      const startDate = moment.tz(selectedDateRange[0], 'Asia/Bangkok').startOf('day');
      const endDate = moment.tz(selectedDateRange[1], 'Asia/Bangkok').endOf('day');

      dateMatch = testDate.isBetween(startDate, endDate, null, '[]');
    }

    const branchMatch = !selectedBranch || Number(score.branch_id) === Number(selectedBranch);

    return searchMatch && typeMatch && dateMatch && branchMatch;
  });
}, [testScores, searchText, selectedTestType, selectedDateRange, selectedBranch]);


  const handleDeleteTestScore = (id) => {
    if (!id) {
      console.error("Test Score ID is undefined, cannot delete.");
      notification.error({ message: t('Error: Test Score ID is missing') });
      return;
    }
  
    Modal.confirm({
      title: t('Are you sure you want to delete this test score?'),
      content: t('This action cannot be undone.'),
      okText: t('Yes, Delete'),
      okType: 'danger',
      cancelText: t('Cancel'),
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/test-scores/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
  
          fetchTestScores(); // Refresh data after deletion
          notification.success({ message: t('Test score deleted successfully') });
        } catch (error) {
          console.error('Error deleting test score:', error);
          notification.error({ message: t('Error deleting test score') });
        }
      }
    });
  };
  
      const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", 
};


  const columns = [
     {
        title: t("Branch"),
        key: "branch",
        dataIndex: "branch_name", // ✅ directly map branch_name
        width: 120,
        ellipsis: false,
        render: (_, record) => {
          const branchId = record.branch_id;
          const color = branchColors[branchId] || "default";
          return record.branch_name ? (
            <Tag color={color}>{record.branch_name}</Tag>
          ) : (
            <Tag>-</Tag>
          );
        },
      },
    { title: t('Student'), dataIndex: 'firstname', key: 'firstname', render: (_, record) => `${record.firstname} ${record.lastname}` },
    { title: t('Nickname'), dataIndex: 'nickname', key: 'nickname' },
    { title: t('Test Date'), dataIndex: 'test_date', key: 'test_date', render: formatDate },
    { title: t('Type'), dataIndex: 'type', key: 'type' },
    { title: t('Subject'), dataIndex: 'sub_type', key: 'sub_type' },
    { title: t('Details'), dataIndex: 'test_name', key: 'test_name', render: (text) => text || '-' },
    { title: t('Score'), dataIndex: 'score', key: 'score' },
    { title: t('Note'), dataIndex: 'note', key: 'note' },
    {
      title: t('Actions'),
      key: 'actions',
      render: (_, record) => (
        <Button danger onClick={() => handleDeleteTestScore(record.id)}>
          {t('Delete')}
        </Button>
      ),
    },
  ];

  return (
        <Card>
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: 16 
                }}
              >
                <Title level={3} style={{ margin: 0 }}>
                  <BarChartOutlined /> Student Test Scores
                </Title>
                <Button 
                  type="primary" 
                  onClick={() => setIsModalVisible(true)}
                >
                  {t('Add Student Score')}
                </Button>
              </div>

              <Layout>
                <Content>
        <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Card>
                  <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 16 }}>
      

                  {/* Branch Filter (Superadmin Only) */}
                  {userRole === "superadmin" && (
                    <Col xs={24} sm={12} lg={6}>
                      <Select
                        placeholder={t("Select Branch")}
                        style={{ width: '100%' }}
                        allowClear
                        value={selectedBranch}
                        onChange={(val) => setSelectedBranch(val)}
                      >
                        {branches.map((branch) => (
                          <Select.Option key={branch.id} value={branch.id}>
                            {branch.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Col>
                  )}

                  {/* Search Input */}
                  <Col xs={24} sm={12} lg={6}>
                    <Input
                      placeholder={t('Search by name or nickname')}
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                      style={{ width: '100%' }}
                      allowClear
                    />
                  </Col>

                  {/* Date Range Picker */}
                  <Col xs={24} sm={12} lg={6}>
                    <RangePicker
                      value={selectedDateRange}
                      onChange={setSelectedDateRange}
                      placeholder={[t('Start Date'), t('End Date')]}
                      style={{ width: '100%' }}
                    />
                  </Col>

                  {/* Test Type Select */}
                  <Col xs={24} sm={12} lg={6}>
                    <Select
                      placeholder={t('Select Test Type')}
                      value={selectedTestType}
                      onChange={setSelectedTestType}
                      style={{ width: '100%' }}
                      allowClear
                    >
                      {testTypes.map(type => (
                        <Option key={type.name} value={type.name}>
                          {type.name}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                </Row>

                  </Card>
                </Col>
              </Row>
          <Modal
            title={t('Add Student Score')}
            visible={isModalVisible}
            onCancel={() => setIsModalVisible(false)}
            onOk={handleSubmit}
          >
            <div className="form-group">
              <label>{t('Student')}</label>
              <Select
                showSearch
                value={formData.student_id}
                onChange={(value) => handleFormChange('student_id', value)}
                placeholder={t('Select Student')}
                style={{ width: '100%' }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {students.map(student => (
                  <Option key={student.userid} value={student.userid}>
                    {`${student.firstname} ${student.lastname} (${student.nickname})`}
                  </Option>
                ))}
              </Select>

            </div>
            <div className="form-group">
                <label>{t('Test Date')}</label>
                <input
                  type="date"
                  value={formData.test_date || ''}
                  onChange={(e) => {
                    const selectedDate = e.target.value;

                    // Optional: convert to Asia/Bangkok timezone if needed
                    const dateInBangkok = selectedDate
                      ? moment.tz(selectedDate, 'Asia/Bangkok').format('YYYY-MM-DD')
                      : null;

                    handleFormChange('test_date', dateInBangkok);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9',
                  }}
                />
              </div>
            <div className="form-group">
              <label>{t('Test Type')}</label>
              <Select
                value={formData.type}
                onChange={(value) => handleFormChange('type', value)}
                placeholder={t('Select Test Type')}
                style={{ width: '100%' }}
              >
                {testTypes.map((type) => (
                  <Option key={type.name} value={type.name}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </div>
            {formData.type && (
              <div className="form-group">
                <label>{t('Subject')}</label>
                <Select
                  value={formData.sub_type}
                  onChange={(value) => handleFormChange('sub_type', value)}
                  placeholder={t('Select Subject')}
                  style={{ width: '100%' }}
                >
                  {getSubtypes().map((subtype, index) => (
                    <Option key={index} value={subtype}>
                      {subtype}
                    </Option>
                  ))}
                </Select>
              </div>
            )}
            <div className="form-group">
              <label>{t('Details')}</label>
              <Input
                value={formData.test_name || ''} // Allow Details to be empty
                onChange={(e) => handleFormChange('test_name', e.target.value)}
                placeholder={t('Enter test details')}
              />
            </div>
            <div className="form-group">
              <label>{t('Score')}</label>
              <Input
                value={formData.score}
                onChange={(e) => handleFormChange('score', e.target.value)}
                placeholder={t('Enter score')}
              />
            </div>
            <div className="form-group">
              <label>{t('Note')}</label>
              <Input.TextArea
                value={formData.note}
                onChange={(e) => handleFormChange('note', e.target.value)}
                rows={4}
                placeholder={t('Enter any additional notes')}
              />
            </div>
            {error && <div className="error-message">{error}</div>}
          </Modal>
          <Table
            columns={columns}
            dataSource={getFilteredData()}
            pagination={{
              current: currentPage,
              pageSize: itemsPerPage,
              total: getFilteredData().length,
              onChange: setCurrentPage,
            }}
            rowKey="id"
          />
        </Content>
      </Layout>
    </Card>
  );
};

export default withRoleAccess(TestScore, ['admin', 'superadmin']);
